import type { FlowResult } from '../../../../../shared/player-contracts/src'
import { computed, onMounted, ref, watch } from 'vue'
import type { DeckCardInput, DeckElementInteraction } from '../../deck/types'
import type { DeckTemplateSettings } from '../../deck/templateSettings'
import { postDeckPlayerCardOpened, postDeckPlayerCheckboxState } from '../api/deckPlayerApi'
import type { FlowSessionState } from '../flowchart/runtime/sessionState'
import type { PrimaryCheckboxRef } from '../helpers/primaryCheckbox'
import type { useDeckPlayerStore } from '../store/useDeckPlayerStore'
import type { DeckPlayerModel } from '../types'

type Props = {
  deckId: string
  model: DeckPlayerModel
  cards: DeckCardInput[]
  forceComplete: boolean
  allowReplayWhenCompleted: boolean
  retryAllowed: boolean
  retryBlockedUntil: string | null
  flowPropagationBlockedUntil: string | null
  suppressProgressWrites: boolean
  deckTemplate: DeckTemplateSettings
  initialValues: Record<string, boolean[]>
  isLoggedIn: boolean
  restRoot?: string
  nonce?: string
}

type Emit = {
  (e: 'card-removed', payload: { cardId: string }): void
  (e: 'retry', payload: { deckId: string }): void
  (e: 'completed', payload: { deckId: string }): void
  (e: 'flow-end', payload: { deckId: string; result: FlowResult }): void
}

type Deps = {
  props: Props
  emit: Emit
  store: ReturnType<typeof useDeckPlayerStore>
  flow: FlowSessionState
  t: (key: string) => string
  fireConfetti: () => Promise<void>
  /** False when the flowchart graph payload could not be read. */
  hasValidFlowGraph: () => boolean
  primaryRef: () => PrimaryCheckboxRef | null
  debugLog: (label: string, data?: unknown) => void
  debugWarn: (label: string, data?: unknown) => void
}

const nowMs = (): number => {
  try {
    if (typeof performance !== 'undefined' && typeof performance.now === 'function') {
      return performance.now()
    }
  } catch {
    // ignore
  }
  return Date.now()
}

/**
 * Which card is active and how far the learner is, plus the progress REST writes:
 * card-opened events, checkbox states with time spent, and completion events.
 * Checkbox decks keep their queue in the store; flowcharts in the flow session.
 */
export function usePlayerProgress(deps: Deps) {
  const { props, emit, store, flow } = deps
  const isCheckboxModel = computed(() => props.model === 'checkbox')

  watch(
    () => [props.deckId, props.model, props.cards] as const,
    () => {
      if (!isCheckboxModel.value || props.forceComplete) return
      store.initSession(props.deckId, props.cards, {
        initialValues: props.isLoggedIn ? props.initialValues : {},
      })
    },
    { immediate: true },
  )

  const session = computed(() => store.getSession(props.deckId))

  const total = computed(() => {
    if (props.model === 'flowchart') return flow.progressTotal.value ?? props.cards.length
    return session.value?.total ?? props.cards.length
  })

  const completed = computed(() => {
    if (props.forceComplete) return total.value
    if (props.model === 'flowchart')
      return flow.progressCurrent.value ?? flow.doneCardIds.value.size
    return session.value ? Object.keys(session.value.doneIds).length : 0
  })

  const activeCardId = computed(() =>
    props.model === 'flowchart' ? flow.currentCardId.value : (session.value?.queue?.[0] ?? null),
  )
  const activeCard = computed(() => props.cards.find((c) => c.id === activeCardId.value) ?? null)
  /** REST card id: the WordPress post id when known. */
  const activeApiCardId = computed(() => {
    const card = activeCard.value
    if (card && typeof card.postId === 'number' && Number.isFinite(card.postId))
      return String(card.postId)
    return activeCardId.value
  })

  const activeValues = computed(() => {
    const id = activeCardId.value
    if (!id) return []
    if (props.model === 'flowchart') return flow.cardCheckboxValues.value.get(id) ?? []
    return Array.isArray(session.value?.valuesByCard?.[id]) ? session.value!.valuesByCard[id] : []
  })

  // Time on the current card, sent with checkbox states.
  const lastOpenedCardId = ref<string | null>(null)
  const lastOpenedAtMs = ref<number | null>(null)
  const timeCursorMs = ref<number | null>(null)

  onMounted(() => {
    deps.debugLog('mounted', {
      deckId: props.deckId,
      model: props.model,
      isLoggedIn: props.isLoggedIn,
      suppressProgressWrites: props.suppressProgressWrites,
      forceComplete: props.forceComplete,
      restRootProvided: Boolean(props.restRoot),
      nonceProvided: Boolean(props.nonce),
      cards: props.cards.length,
    })
  })

  watch(
    () => activeCardId.value,
    (cardId) => {
      const now = nowMs()
      timeCursorMs.value = now
      if (!cardId) return
      if (!isCheckboxModel.value && props.model !== 'flowchart') return
      deps.debugLog('active card changed', {
        deckId: props.deckId,
        activeCardId: cardId,
        activeApiCardId: activeApiCardId.value,
        activePostId: activeCard.value?.postId ?? null,
        isLoggedIn: props.isLoggedIn,
        suppressProgressWrites: props.suppressProgressWrites,
        restRootProvided: Boolean(props.restRoot),
        nonceProvided: Boolean(props.nonce),
      })
      if (props.suppressProgressWrites) {
        deps.debugLog('skip card-opened POST', { reason: 'suppressed' })
        return
      }
      // Avoid accidental double-fires caused by reactive churn.
      if (
        lastOpenedCardId.value === cardId &&
        lastOpenedAtMs.value !== null &&
        now - lastOpenedAtMs.value < 750
      ) {
        return
      }
      lastOpenedCardId.value = cardId
      lastOpenedAtMs.value = now
      const apiCardId = activeApiCardId.value
      if (!apiCardId) return
      void postDeckPlayerCardOpened({
        deckId: props.deckId,
        model: props.model,
        cardId: apiCardId,
        restRoot: props.restRoot,
        nonce: props.nonce,
      }).catch((e) => {
        deps.debugWarn('POST /player/card-opened failed', e)
        if (import.meta.env.DEV) console.warn('Deckplayer: failed to POST /player/card-opened', e)
      })
    },
    { immediate: true },
  )

  const modelError = computed(() => {
    // Without a configured graph the flowchart model uses a default "play all cards" flow.
    if (props.model === 'flowchart') {
      return deps.hasValidFlowGraph() ? null : 'Flowchart model: invalid flowGraph payload.'
    }
    if (props.model !== 'checkbox') return deps.t('deckPlayer.shell.unsupportedModel')
    return null
  })

  const isFlowEnd = computed(() => props.model === 'flowchart' && flow.complete.value)
  const isPhaseInterlude = computed(() => props.model === 'flowchart' && flow.phaseInterlude.value)
  const showFlowNextButton = computed(() => props.deckTemplate?.showFlowNextButton === true)

  /** With deck propagation on, Continue after a flow end may be blocked until a time. */
  const flowPropagationBlocked = computed(() => {
    if (props.deckTemplate?.deckPropagation !== true) return false
    const until = props.flowPropagationBlockedUntil
    if (!until) return false
    const ms = new Date(until).getTime()
    return Number.isFinite(ms) && Date.now() < ms
  })

  const isComplete = computed(() =>
    props.model === 'flowchart'
      ? props.forceComplete
      : props.forceComplete || (session.value?.queue?.length ?? 0) === 0,
  )

  const hasFiredCompletion = ref(false)
  const hasBeenIncomplete = ref(false)
  watch(
    () => isComplete.value,
    (complete) => {
      if (!complete) {
        hasBeenIncomplete.value = true
        hasFiredCompletion.value = false
        return
      }
      // Already complete on first render (e.g. reload): don't celebrate again.
      if (!hasBeenIncomplete.value || hasFiredCompletion.value) return
      hasFiredCompletion.value = true
      emit('completed', { deckId: props.deckId })
      void deps.fireConfetti()
    },
    { immediate: true },
  )

  watch(
    () => isFlowEnd.value,
    (end) => {
      if (!end) return
      emit('flow-end', {
        deckId: props.deckId,
        result: {
          answers: Object.fromEntries(flow.cardAnswers.value),
          signals: [...flow.flowSignals.value],
        },
      })
    },
  )

  const requestRetry = () => {
    if (!props.allowReplayWhenCompleted || !props.retryAllowed) return
    emit('retry', { deckId: props.deckId })
  }

  const retryBlockedText = computed(() => {
    if (props.retryAllowed) return ''
    const date = props.retryBlockedUntil ? new Date(props.retryBlockedUntil) : null
    if (!date || !Number.isFinite(date.getTime())) return 'You can continue in the next session.'
    try {
      const formatted = new Intl.DateTimeFormat(undefined, {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      }).format(date)
      return `Available again: ${formatted}`
    } catch {
      return `Available again: ${props.retryBlockedUntil}`
    }
  })

  /** Checkbox model: posts the primary checkbox state and advances the store queue. */
  const handleCheckboxInteraction = (interaction: DeckElementInteraction) => {
    if (!isCheckboxModel.value) return
    const cardId = activeCardId.value
    const apiCardId = activeApiCardId.value
    const primary = deps.primaryRef()
    const isPrimary =
      Boolean(cardId) &&
      interaction.elementType === 'checkbox-group' &&
      Boolean(primary) &&
      interaction.face === primary!.face &&
      interaction.elementIndex === primary!.elementIndex

    if (interaction.elementType === 'checkbox-group') {
      deps.debugLog('checkbox-group interaction', {
        deckId: props.deckId,
        isLoggedIn: props.isLoggedIn,
        suppressProgressWrites: props.suppressProgressWrites,
        restRootProvided: Boolean(props.restRoot),
        nonceProvided: Boolean(props.nonce),
        activeCardId: cardId,
        activeApiCardId: apiCardId,
        activePostId: activeCard.value?.postId ?? null,
        primaryRef: primary,
        interaction,
        isPrimaryCheckboxInteraction: isPrimary,
        willPost:
          isPrimary &&
          props.isLoggedIn &&
          Boolean(cardId) &&
          Boolean(apiCardId) &&
          !props.suppressProgressWrites,
        reasons: {
          notPrimary: !isPrimary,
          notLoggedIn: !props.isLoggedIn,
          missingCardId: !cardId,
          missingApiCardId: !apiCardId,
          suppressed: props.suppressProgressWrites,
        },
      })
    }

    if (isPrimary && props.isLoggedIn && cardId && apiCardId && !props.suppressProgressWrites) {
      const detail = interaction.detail as { values?: unknown }
      const values = Array.isArray(detail?.values) ? detail.values.map(Boolean) : []
      const now = nowMs()
      const addTimeMs =
        typeof timeCursorMs.value === 'number' && Number.isFinite(timeCursorMs.value)
          ? Math.max(0, Math.round(now - timeCursorMs.value))
          : 0
      timeCursorMs.value = now
      void postDeckPlayerCheckboxState({
        deckId: props.deckId,
        model: props.model,
        cardId: apiCardId,
        values,
        addTimeMs,
        restRoot: props.restRoot,
        nonce: props.nonce,
      }).catch((e) => {
        deps.debugWarn('POST /player/checkbox-state failed', e)
        if (import.meta.env.DEV)
          console.warn('Deckplayer: failed to POST /player/checkbox-state', e)
      })
    }

    const result = store.handleElementInteraction(props.deckId, props.cards, interaction)
    if (result.removedCardId) emit('card-removed', { cardId: result.removedCardId })
  }

  return {
    total,
    completed,
    activeCardId,
    activeCard,
    activeValues,
    modelError,
    isFlowEnd,
    isPhaseInterlude,
    showFlowNextButton,
    flowPropagationBlocked,
    requestRetry,
    retryBlockedText,
    handleCheckboxInteraction,
  }
}
